/**
 * PC Pawn Component
 *
 * Retro computer-themed pawn: a character body topped with a CRT monitor.
 * Based on the Blue variant SVG template with colors parameterized via PcPawnColorScheme.
 *
 * ViewBox: 0 0 323 473 (matches other skins for consistent road section scaling)
 */

import React from 'react';
import type { PawnProps } from '../../constants/types';
import type { PcPawnColorScheme } from './constants/pawnColorSchemes';

interface PcPawnInternalProps extends PawnProps {
  colorScheme: PcPawnColorScheme;
}

const PcPawnComponent = React.memo(function PcPawnComponent({
  viewBox = '0 0 323 473',
  className = '',
  colorScheme
}: PcPawnInternalProps) {
  const p = `${colorScheme.name}PcPawn`;

  return (
    <svg
      viewBox={viewBox}
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label={`${colorScheme.name} pc pawn`}
    >
      <defs>
        {/* Shadow gradient */}
        <linearGradient
          id={`${p}_shadow`}
          x1="-346.85"
          y1="6039.17"
          x2="-346.85"
          y2="6401.82"
          gradientTransform="translate(881.17 -5948.11) rotate(3.66)"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset=".54" stopColor="#000" stopOpacity="0" />
          <stop offset="1" stopColor="#000" />
        </linearGradient>

        {/* Body outer gradient */}
        <linearGradient
          id={`${p}_bodyOuter`}
          x1="-458.16"
          y1="6306.87"
          x2="-235.54"
          y2="6306.87"
          gradientTransform="translate(6439.1 670.58) rotate(90)"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor={colorScheme.bodyOuterGradient[0]} />
          <stop offset=".24" stopColor={colorScheme.bodyOuterGradient[1]} />
          <stop offset=".47" stopColor={colorScheme.bodyOuterGradient[2]} />
          <stop offset=".75" stopColor={colorScheme.bodyOuterGradient[3]} />
          <stop offset="1" stopColor={colorScheme.bodyOuterGradient[4]} />
        </linearGradient>

        {/* Body inner gradient */}
        <linearGradient
          id={`${p}_bodyInner`}
          x1="-348.06"
          y1="6178.38"
          x2="-348.06"
          y2="6381.7"
          gradientTransform="translate(6410.65 642.67) rotate(90)"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor={colorScheme.bodyInnerGradient[0]} />
          <stop offset=".24" stopColor={colorScheme.bodyInnerGradient[1]} />
          <stop offset=".47" stopColor={colorScheme.bodyInnerGradient[2]} />
          <stop offset=".75" stopColor={colorScheme.bodyInnerGradient[3]} />
          <stop offset="1" stopColor={colorScheme.bodyInnerGradient[4]} />
        </linearGradient>

        {/* Body shadow gradient */}
        <linearGradient
          id={`${p}_bodyShadow`}
          x1="-419.06"
          y1="6258.28"
          x2="-274.64"
          y2="6258.28"
          gradientTransform="translate(6393.62 622.09) rotate(90)"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor={colorScheme.bodyShadowGradient[0]} />
          <stop offset=".51" stopColor={colorScheme.bodyShadowGradient[1]} />
          <stop offset="1" stopColor={colorScheme.bodyShadowGradient[2]} />
        </linearGradient>

        {/* Shirt gradient */}
        <linearGradient
          id={`${p}_shirt`}
          x1="-161.33"
          y1="6043.41"
          x2="-20.97"
          y2="6043.41"
          gradientTransform="translate(799.98 -5771.3) rotate(5.55)"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor={colorScheme.shirtGradient[0]} />
          <stop offset=".27" stopColor={colorScheme.shirtGradient[1]} />
          <stop offset=".49" stopColor={colorScheme.shirtGradient[2]} />
          <stop offset=".68" stopColor={colorScheme.shirtGradient[3]} />
          <stop offset=".86" stopColor={colorScheme.shirtGradient[4]} />
          <stop offset="1" stopColor={colorScheme.shirtGradient[5]} />
        </linearGradient>

        {/* Screen dark gradient */}
        <linearGradient
          id={`${p}_screen`}
          x1="41.67"
          y1="120.77"
          x2="259.21"
          y2="120.77"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor={colorScheme.screenGradient[0]} />
          <stop offset=".23" stopColor={colorScheme.screenGradient[1]} />
          <stop offset=".59" stopColor={colorScheme.screenGradient[2]} />
          <stop offset="1" stopColor={colorScheme.screenGradient[3]} />
        </linearGradient>
      </defs>

      {/* Scale up content to fill viewBox similarly to other pawn skins */}
      <g transform="translate(25, 0)">
        {/* Character body group */}
        <g>
          {/* Body shadow */}
          <path
            fill={`url(#${p}_shadow)`}
            d="M133.44,429.84c-2.64,0-5.33-.09-7.98-.26-30.43-1.95-58.34-14.76-78.6-36.08-20.32-21.38-30.51-48.75-28.7-77.07.92-14.44,4.9-28.24,11.8-41.02,3.74-20.57,13.26-39.3,27.61-54.35-1-1.26-1.99-2.55-2.97-3.92-9.92-13.77-16.4-29.87-18.73-46.58-1.16-8.11-1.33-16.46-.53-24.78,1.59-16.37,6.81-31.69,15.51-45.54,8.4-13.39,19.96-24.9,33.43-33.29,16.61-10.36,35.72-15.84,55.25-15.84,3.4,0,6.85.17,10.25.5,6.95.68,13.71,2.01,20.1,3.96,6.05,1.82,11.95,4.19,17.54,7.05,5.4,2.76,10.7,6.1,15.73,9.92,9.45,7.19,17.54,15.87,24.08,25.8,6.59,10.02,11.38,20.99,14.23,32.61l.12.51v.02c.38,1.58.74,3.22,1.08,5.02,1.61,8.56,2.14,17.32,1.58,26.05-.07,1.16-.17,2.32-.28,3.5-1.54,15.96-6.55,30.95-14.87,44.53-2.07,3.4-4.39,6.73-6.88,9.9-2.26,2.88-4.66,5.64-7.17,8.22,12.03,15.39,19.55,33.7,21.81,53.15,7.27,15.39,10.55,32.38,9.47,49.17-1.73,27.03-14.35,52.05-35.53,70.46-21.02,18.27-48.49,28.34-77.33,28.34Z"
          />

          {/* Body outer ellipse */}
          <ellipse
            fill={`url(#${p}_bodyOuter)`}
            cx="132.23"
            cy="323.73"
            rx="103.08"
            ry="111.31"
            transform="translate(-199.28 435.02) rotate(-86.34)"
          />

          {/* Body dark inner ellipse */}
          <ellipse
            fill={colorScheme.darkerFill}
            cx="132.26"
            cy="304.39"
            rx="103.08"
            ry="101.66"
            transform="translate(-179.95 416.93) rotate(-86.34)"
          />

          {/* Body inner gradient ellipse */}
          <ellipse
            fill={`url(#${p}_bodyInner)`}
            cx="132.88"
            cy="294.61"
            rx="103.08"
            ry="101.66"
            transform="translate(-169.61 408.41) rotate(-86.34)"
          />

          {/* Face circle */}
          <path
            fill={colorScheme.bodyHighlight}
            d="M135.05,354.48c-1.46,0-2.95-.05-4.42-.14-18.54-1.19-35.49-9.67-47.74-23.88-12.24-14.2-18.31-32.43-17.1-51.33,2.39-37.27,32.91-66.47,69.48-66.47,1.46,0,2.95.05,4.42.14,18.54,1.19,35.49,9.67,47.74,23.88,12.24,14.2,18.31,32.43,17.1,51.33-2.39,37.27-32.91,66.47-69.48,66.47Z"
          />

          {/* Face shadow ring */}
          <ellipse
            fill="black"
            cx="135.08"
            cy="279.25"
            rx="73.22"
            ry="72.21"
            transform="translate(-152.22 396.22) rotate(-86.34)"
          />

          {/* Face shadow inner */}
          <ellipse
            opacity="0.57"
            fill={`url(#${p}_bodyShadow)`}
            cx="135.34"
            cy="275.24"
            rx="73.22"
            ry="72.21"
            transform="translate(-147.98 392.72) rotate(-86.34)"
          />

          {/* Body shirt highlight */}
          <path
            fill={colorScheme.bodyGlow}
            d="M61.01,207.36l26.84,91.91c5.41,18.54,21.56,31.91,40.78,33.78h0c21.62,2.1,41.89-10.82,49.12-31.31l30.7-87.04c10.28-29.16-9.76-60.18-40.57-62.78l-57.54-4.87c-33.06-2.8-58.63,28.47-49.33,60.32Z"
          />

          {/* Body shirt gradient */}
          <path
            fill={`url(#${p}_shirt)`}
            d="M61.33,202.29l26.84,91.91c5.41,18.54,21.56,31.91,40.78,33.78h0c21.62,2.1,41.89-10.82,49.12-31.31l30.7-87.04c10.28-29.16-9.76-60.18-40.57-62.78l-57.54-4.87c-33.06-2.8-58.63,28.47-49.33,60.32Z"
          />
        </g>

        {/* Monitor group */}
        <g>
          {/* Monitor case back */}
          <path
            fill="#bab7a8"
            d="M288.77,90.62v88.33c0,4.86-3.03,8.79-6.76,8.79H98.94c-3.74,0-6.77-3.93-6.77-8.79V62.6c0-4.86,3.03-8.79,6.77-8.79h161.49c15.32,0,27.81,15.81,28.31,35.56.02.41.02.83.02,1.25Z"
          />

          {/* Ports (dark shadow) */}
          <g>
            <path
              fill="#5e5e5e"
              d="M288.77,90.62v3.8h-30.04c-1.08,0-1.94-1.13-1.94-2.52,0-.7.22-1.33.57-1.79.35-.46.83-.74,1.37-.74h30.02c.02.41.02.83.02,1.25Z"
            />
            <path
              fill="#5e5e5e"
              d="M288.77,99.23v5.05h-30.04c-1.08,0-1.94-1.13-1.94-2.52,0-.7.22-1.33.57-1.78.35-.46.83-.74,1.37-.74h30.04Z"
            />
            <path
              fill="#5e5e5e"
              d="M288.77,109.09v5.06h-30.04c-1.08,0-1.94-1.13-1.94-2.53,0-.7.22-1.32.57-1.78.35-.46.83-.74,1.37-.74h30.04Z"
            />
          </g>

          {/* Ports (light) */}
          <g>
            <path
              fill="#a8b0a2"
              d="M288.77,89.74v3.8h-30.04c-1.08,0-1.94-1.13-1.94-2.52,0-.7.22-1.33.57-1.79.35-.46.83-.74,1.37-.74h30.02c.02.41.02.83.02,1.25Z"
            />
            <path
              fill="#a8b0a2"
              d="M288.77,98.35v5.05h-30.04c-1.08,0-1.94-1.13-1.94-2.52,0-.7.22-1.33.57-1.78.35-.46.83-.74,1.37-.74h30.04Z"
            />
            <path
              fill="#a8b0a2"
              d="M288.77,108.21v5.06h-30.04c-1.08,0-1.94-1.13-1.94-2.53,0-.7.22-1.32.57-1.78.35-.46.83-.74,1.37-.74h30.04Z"
            />
          </g>

          {/* Monitor bezel */}
          <rect
            fill="#f9f4d9"
            x="71.68"
            y="21.63"
            width="196.6"
            height="198.99"
            rx="13.41"
            ry="13.41"
          />

          {/* Monitor screen frame */}
          <path
            fill={colorScheme.screenFrame}
            d="M42.01,21.63h183.19v198.99H42.01c-7.4,0-13.41-6.01-13.41-13.41V35.04c0-7.4,6.01-13.41,13.41-13.41Z"
          />

          {/* Screen (dark gradient) */}
          <rect
            fill={`url(#${p}_screen)`}
            x="41.67"
            y="35.4"
            width="170.46"
            height="170.75"
            rx="17.03"
            ry="17.03"
          />

          {/* Side buttons (dark shadow) */}
          <g>
            <rect
              fill="#5e5e5e"
              x="229.78"
              y="161.94"
              width="29.09"
              height="8.74"
              rx="3.77"
              ry="3.77"
            />
            <rect
              fill="#5e5e5e"
              x="229.78"
              y="178.99"
              width="29.09"
              height="8.74"
              rx="3.77"
              ry="3.77"
            />
            <rect
              fill="#5e5e5e"
              x="229.78"
              y="196.05"
              width="29.09"
              height="8.74"
              rx="3.77"
              ry="3.77"
            />
          </g>

          {/* Side buttons (light) */}
          <g>
            <rect
              fill="#a8b0a2"
              x="232.62"
              y="161.94"
              width="29.09"
              height="8.74"
              rx="3.77"
              ry="3.77"
            />
            <rect
              fill="#a8b0a2"
              x="232.62"
              y="178.99"
              width="29.09"
              height="8.74"
              rx="3.77"
              ry="3.77"
            />
            <rect
              fill="#a8b0a2"
              x="232.62"
              y="196.05"
              width="29.09"
              height="8.74"
              rx="3.77"
              ry="3.77"
            />
          </g>

          {/* Screen accent elements (USB ports / power indicator) */}
          <rect
            fill={colorScheme.accent}
            x="80.81"
            y="84.72"
            width="11.37"
            height="34.07"
            rx="5.68"
            ry="5.68"
          />
          <rect
            fill={colorScheme.accent}
            x="130.15"
            y="84.72"
            width="11.37"
            height="34.07"
            rx="5.68"
            ry="5.68"
          />
          <rect
            fill={colorScheme.accent}
            x="112.57"
            y="108.23"
            width="3.6"
            height="80.79"
            transform="translate(263 34.26) rotate(90)"
          />
          <rect fill={colorScheme.accent} x="71.2" y="143.07" width="2.77" height="7.36" />
          <rect fill={colorScheme.accent} x="153.38" y="143.07" width="2.77" height="7.36" />

          {/* Cursor/pointer lines */}
          <polyline
            fill="none"
            stroke={colorScheme.strokeColor}
            strokeMiterlimit="10"
            points="212.13 78.59 207.1 78.7 207.15 84.72 194.49 67.39 161.82 67.39 166.71 51.84 152.12 36.59 152.12 35.4"
          />
          <polyline
            fill="none"
            stroke={colorScheme.strokeColor}
            strokeMiterlimit="10"
            points="176.29 67.39 185.51 54.61 207.4 51.17 202.62 44.83 207.4 40.65"
          />

          {/* Cursor arrow */}
          <path
            fill={colorScheme.darkFill}
            d="M174.01,67.78l2.08-1.53c.12-.09.23-.2.33-.32l2.39-2.03-.51,2.56c.02.15.05.3.11.44l1.69.43-3.04.33c-.14-.06-.61.02-.76,0l-2.28.1Z"
          />
        </g>
      </g>
    </svg>
  );
});

PcPawnComponent.displayName = 'PcPawnComponent';

export default PcPawnComponent;
